<template>
  <div class="upload-progress-container">
    <!-- Upload Progress Bar -->
    <div class="w-full bg-gray-200 rounded-full h-2 mb-2" v-if="showProgress">
      <div 
        class="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
        :class="{
          'bg-green-600': state === UploadState.COMPLETED,
          'bg-red-600': state === UploadState.ERROR,
          'bg-yellow-600': state === UploadState.PENDING || state === UploadState.CREATING_RECORD
        }"
        :style="`width: ${progress}%`"
      ></div>
    </div>

    <!-- Status Message -->
    <div class="flex items-center justify-between text-sm">
      <div class="flex items-center space-x-2">
        <!-- State Icon -->
        <div class="flex-shrink-0">
          <!-- Pending/Creating Record -->
          <svg 
            v-if="state === UploadState.PENDING || state === UploadState.CREATING_RECORD"
            class="w-4 h-4 text-yellow-600 animate-spin" 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>

          <!-- Uploading -->
          <svg 
            v-else-if="state === UploadState.UPLOADING || state === UploadState.COMPLETING"
            class="w-4 h-4 text-blue-600 animate-pulse" 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>

          <!-- Completed -->
          <svg 
            v-else-if="state === UploadState.COMPLETED"
            class="w-4 h-4 text-green-600" 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>

          <!-- Error -->
          <svg 
            v-else-if="state === UploadState.ERROR"
            class="w-4 h-4 text-red-600" 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>

          <!-- Cancelled -->
          <svg 
            v-else-if="state === UploadState.CANCELLED"
            class="w-4 h-4 text-gray-600" 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>

        <!-- Status Text -->
        <span 
          class="text-sm"
          :class="{
            'text-gray-600': state === UploadState.PENDING || state === UploadState.CREATING_RECORD,
            'text-blue-600': state === UploadState.UPLOADING || state === UploadState.COMPLETING,
            'text-green-600': state === UploadState.COMPLETED,
            'text-red-600': state === UploadState.ERROR,
            'text-gray-600': state === UploadState.CANCELLED
          }"
        >
          {{ message || getDefaultMessage() }}
        </span>
      </div>

      <!-- Progress Percentage -->
      <span 
        v-if="showProgress && progress > 0" 
        class="text-xs text-gray-500 font-medium"
      >
        {{ Math.round(progress) }}%
      </span>
    </div>

    <!-- Error Details (expandable) -->
    <div v-if="state === UploadState.ERROR && error" class="mt-2">
      <button
        @click="showErrorDetails = !showErrorDetails"
        class="text-xs text-red-600 hover:text-red-800 focus:outline-none"
      >
        {{ showErrorDetails ? 'Hide details' : 'Show error details' }}
      </button>
      
      <div v-if="showErrorDetails" class="mt-1 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
        {{ error }}
      </div>
    </div>

    <!-- Retry Button -->
    <div v-if="state === UploadState.ERROR && allowRetry" class="mt-2">
      <button
        @click="$emit('retry')"
        class="text-xs bg-red-100 text-red-700 hover:bg-red-200 px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
      >
        Retry Upload
      </button>
    </div>
  </div>
</template>

<script>
import { UploadState } from '@/services/upload-service';

export default {
  name: 'UploadProgress',
  props: {
    state: {
      type: String,
      default: UploadState.PENDING,
      validator: (value) => Object.values(UploadState).includes(value)
    },
    progress: {
      type: Number,
      default: 0,
      validator: (value) => value >= 0 && value <= 100
    },
    message: {
      type: String,
      default: ''
    },
    error: {
      type: [String, Error],
      default: null
    },
    showProgress: {
      type: Boolean,
      default: true
    },
    allowRetry: {
      type: Boolean,
      default: true
    }
  },
  emits: ['retry'],
  data() {
    return {
      UploadState,
      showErrorDetails: false
    };
  },
  methods: {
    getDefaultMessage() {
      switch (this.state) {
        case UploadState.PENDING:
          return 'Preparing upload...';
        case UploadState.CREATING_RECORD:
          return 'Creating file record...';
        case UploadState.UPLOADING:
          return 'Uploading file...';
        case UploadState.COMPLETING:
          return 'Finalizing upload...';
        case UploadState.COMPLETED:
          return 'Upload completed successfully';
        case UploadState.ERROR:
          return 'Upload failed';
        case UploadState.CANCELLED:
          return 'Upload cancelled';
        default:
          return 'Unknown state';
      }
    }
  }
};
</script>

<style scoped>
.upload-progress-container {
  @apply w-full;
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: .5;
  }
}
</style>